import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import { PAGE_VERSION } from '../composers/pageEditorCache';
import useVersionActions, { nextVersionId } from '../composers/useVersionActions';
import { diffPages, suggestNote, DiffComponent, PageDiff, SectionStatus } from '~/lib/pageDiff';
import { customWidgetNames, publishChecks, PublishCheck, WidgetLive } from './publishChecks';
import { SLOT_LABELS } from './outline';
import { S, A, TONES, DARK, labelStyle } from './shellTokens';

type Slot = { slotId: string; components: Array<DiffComponent | null> };

// Read when the sheet opens: the review state may have changed since the editor loaded the page.
const PAGE_STATUS = gql`
  query publishPageStatus($id: ID!) {
    page(id: $id) { _id state publishedVersion commitedVersion canUserRequestApproval }
  }
`;

const WIDGET_LIVE_VERSIONS = gql`
  query customWidgetLiveVersions($names: [String!]!) {
    customWidgetLiveVersions(names: $names) { name isActive liveVersion }
  }
`;

const EMPTY_DIFF: PageDiff = { sections: [], settings: [], unchanged: 0 };
type Draft = { _id: string; slots: Slot[]; template: Record<string, any>; metadata?: Array<{ key: string; value: string }> }; // eslint-disable-line @typescript-eslint/no-explicit-any
type Page = {
  _id: string;
  publishedVersion?: string | null;
  commitedVersion?: string | null;
  state?: string | null;
  canUserRequestApproval?: boolean;
  metadata?: Array<{ key: string; value: string }> | null;
};

type Props = {
  page: Page;
  draft: Draft;
  /** Ask for review of this saved version; otherwise save the draft as a new version. */
  versionId?: string | null;
  versions: Array<{ versionId: string }>;
  lastUpdatedAt?: Date;
  /**
   * After an "outdated" refusal: the page's current timestamp when only the timestamp moved
   * (a review, approval or deploy elsewhere), or null when the content changed too.
   */
  refreshUpdatedAt?: () => Promise<Date | null>;
  /** A save is in flight: wait, or the version would be rejected as outdated. */
  saving?: boolean;
  nameOf: (c: DiffComponent) => string;
  onSelectSection?: (componentId: string) => void;
  onVersionSaved: (updatedAt: string | null) => void;
  onDone: (message: string) => void;
  onClose: () => void;
};

const STATUS: Record<SectionStatus, { label: string; color: string }> = {
  added: { label: 'Added', color: S.green },
  changed: { label: 'Changed', color: S.amber },
  moved: { label: 'Moved', color: S.muted },
  removed: { label: 'Removed', color: S.red }
};
const LAMP: Record<PublishCheck['level'], string> = { block: S.red, warn: S.amber, ok: S.green };

const useStyles = createUseStyles({
  backdrop: { position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(23, 26, 32, 0.25)', display: 'flex', justifyContent: 'flex-end', [DARK]: { background: S.scrim } },
  sheet: {
    width: 460,
    maxWidth: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: S.chrome,
    borderLeft: `1px solid ${S.line}`,
    boxShadow: '-12px 0 32px rgba(23, 26, 32, 0.18)',
    fontFamily: S.ui,
    color: S.fg,
    fontSize: 14,
    [DARK]: { boxShadow: A.pop }
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${S.line}` },
  title: { fontSize: 18, fontWeight: 600 },
  close: { all: 'unset', cursor: 'pointer', width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, color: S.muted, '&:hover': { background: S.chromeHover, color: S.fg } },
  body: { flex: 1, overflowY: 'auto', padding: '16px 20px 24px', display: 'grid', gap: 22, alignContent: 'start' },
  label: { ...labelStyle, marginBottom: 8, display: 'flex', justifyContent: 'space-between', gap: 8 },
  mono: { fontFamily: S.mono, fontSize: 12, fontWeight: 400, letterSpacing: 0, textTransform: 'none' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 },
  change: {
    all: 'unset',
    boxSizing: 'border-box',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '74px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 10,
    padding: '6px 8px',
    borderRadius: 6,
    cursor: 'default',
    '&[data-clickable="true"]': { cursor: 'pointer', '&:hover, &:focus-visible': { background: S.chromeHover } }
  },
  // A new page's sections: nothing to compare, so no status column.
  plain: { gridTemplateColumns: 'minmax(0, 1fr) auto' },
  status: { fontFamily: S.label, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  where: { fontSize: 12, color: S.muted },
  muted: { margin: 0, fontSize: 13, color: S.muted, lineHeight: 1.5 },
  check: { display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr)', gap: 10, padding: '6px 0', fontSize: 13, lineHeight: 1.5 },
  checkItems: { margin: '4px 0 0', paddingLeft: 16, color: S.muted },
  field: { display: 'grid', gap: 6, fontSize: 13 },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '8px 10px',
    fontFamily: S.ui,
    fontSize: 14,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 6,
    background: S.field,
    color: S.fg,
    resize: 'vertical',
    '&:focus': { outline: 'none', borderColor: S.fg }
  },
  version: { fontFamily: S.mono, fontSize: 14 },
  error: { margin: 0, padding: '8px 10px', borderRadius: 6, background: '#FBE9E9', color: S.red, fontSize: 13, [DARK]: { background: TONES.red.bg } },
  foot: { display: 'grid', gap: 8, padding: '14px 20px 18px', borderTop: `1px solid ${S.line}` },
  primary: {
    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', textAlign: 'center', padding: '10px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600, background: S.fg, color: S.field,
    '&:hover': { background: S.fgActive },
    '&[disabled]': { opacity: 0.4, cursor: 'default' }
  },
  secondary: {
    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', textAlign: 'center', padding: '9px 14px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: `1px solid ${S.fieldLine}`, background: S.field, color: S.fg,
    '&:hover': { borderColor: S.fg },
    '&[disabled]': { opacity: 0.4, cursor: 'default' }
  }
});

/**
 * Publishing in one place: what changed since the published version, checks,
 * a suggested note, then save the version and ask for review. A page that isn't
 * live (new, or withdrawn) has nothing to compare with, so its sections are listed instead.
 */
const PublishSheet: React.FC<Props> = ({
  page: editorPage, draft, versionId, versions, lastUpdatedAt, refreshUpdatedAt, saving, nameOf, onSelectSection, onVersionSaved, onDone, onClose
}) => {
  const c = useStyles();
  const { saveVersion, requestReview } = useVersionActions(editorPage._id);
  // The editor loaded the page when it opened; a reviewer may have declined or approved since, or a
  // deploy published another version. Read the review state fresh (outside the cache, so the editor's
  // copy of the page isn't replaced under it).
  const status = useQuery(PAGE_STATUS, { variables: { id: editorPage._id }, fetchPolicy: 'no-cache' });
  const page: Page = useMemo(() => {
    const fresh = status.data && status.data.page;
    return fresh
      ? { ...editorPage, state: fresh.state, publishedVersion: fresh.publishedVersion, commitedVersion: fresh.commitedVersion, canUserRequestApproval: fresh.canUserRequestApproval }
      : editorPage;
  }, [editorPage, status.data]);
  const live = useQuery(PAGE_VERSION, { variables: { pageId: page._id, versionId: page.publishedVersion }, skip: status.loading || !page.publishedVersion, fetchPolicy: 'network-only' });
  const target = useQuery(PAGE_VERSION, { variables: { pageId: page._id, versionId }, skip: !versionId, fetchPolicy: 'network-only' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const after = versionId ? target.data && target.data.pageVersion : draft;
  const before = page.publishedVersion ? live.data && live.data.pageVersion : null;
  // Not live (new, or withdrawn): every section would read "Added".
  const isNew = !page.publishedVersion;
  const loading = status.loading || (page.publishedVersion && live.loading) || (versionId && target.loading);
  const loadError = live.error || target.error;
  // The published version failed to load or no longer exists: there's nothing to compare
  // with, but the version can still be saved and reviewed.
  const liveMissing = !status.loading && Boolean(page.publishedVersion) && !live.loading && (Boolean(live.error) || Boolean(live.data && !live.data.pageVersion));

  // Versions keep slots and template only, so that's what's compared.
  const diff = useMemo(() => (!status.loading && after && (before || !page.publishedVersion)
    ? diffPages(before ? { slots: before.slots, template: before.template } : null, { slots: after.slots, template: after.template })
    : null), [status.loading, after, before, page.publishedVersion]);
  const ready = Boolean(diff) || Boolean(liveMissing && after);
  const names = useMemo(() => customWidgetNames(after ? after.slots : []), [after]);
  const widgetLive = useQuery(WIDGET_LIVE_VERSIONS, { variables: { names }, skip: !names.length, fetchPolicy: 'network-only' });
  const widgets = useMemo(() => {
    const list: Array<WidgetLive & { name: string }> | undefined = widgetLive.data && widgetLive.data.customWidgetLiveVersions;
    return list ? list.reduce((m, w) => ({ ...m, [w.name]: { isActive: w.isActive, liveVersion: w.liveVersion } }), {} as Record<string, WidgetLive>) : undefined;
  }, [widgetLive.data]);
  const checks = useMemo(() => (ready && after
    ? publishChecks({ page: { ...page, metadata: draft.metadata }, slots: after.slots, diff: diff || EMPTY_DIFF, nameOf, widgets, compared: Boolean(diff), firstVersion: isNew })
    : []), [ready, diff, after, page, draft.metadata, nameOf, widgets, isNew]);
  const blocked = checks.some(x => x.level === 'block');
  const newVersion = useMemo(() => nextVersionId(versions), [versions]);
  const suggested = useMemo(() => {
    if (!diff) return '';
    if (!isNew) return suggestNote(diff, nameOf);
    return versions.length ? '' : 'First version of this page.';
  }, [diff, nameOf, isNew, versions]);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  useEffect(() => { if (!noteTouched) setNote(suggested); }, [suggested, noteTouched]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const canRequest = Boolean(page.canUserRequestApproval) && !blocked;
  // Set when this sheet saved the version but the review request then failed: retrying asks for review of it.
  const [savedVersion, setSavedVersion] = useState<string | null>(null);
  const reviewVersion = versionId || savedVersion;

  const run = async (ask: boolean) => {
    setBusy(true);
    setError(null);
    let saved = reviewVersion;
    try {
      if (!saved) {
        let updatedAt;
        try {
          updatedAt = await saveVersion(draft, lastUpdatedAt, newVersion, note.trim());
        } catch (e) {
          // Only the page's timestamp moved (as autosave handles it): save again with the current one.
          const current = /outdated/i.test((e as Error).message || '') && refreshUpdatedAt ? await refreshUpdatedAt() : null;
          if (!current) throw e;
          updatedAt = await saveVersion(draft, current, newVersion, note.trim());
        }
        onVersionSaved(updatedAt);
        saved = newVersion;
        setSavedVersion(newVersion);
      }
      if (ask) await requestReview(saved);
      let message = `Saved version ${saved}.`;
      if (ask) message = versionId || savedVersion ? `Asked for review of version ${saved}.` : `Saved version ${saved} and asked for review.`;
      onDone(message);
    } catch (e) {
      setError(saved && !reviewVersion
        ? `Saved version ${saved}, but the review request didn’t go through: ${(e as Error).message}`
        : (e as Error).message);
      setBusy(false);
    }
  };

  const changes = diff ? diff.sections : [];
  return (
    <div className={c.backdrop} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <aside className={c.sheet} role="dialog" aria-modal="true" aria-label="Publish" data-testid="publish-sheet" data-toast-beside>
        <div className={c.head}>
          <span className={c.title}>{versionId ? `Ask for review of v${versionId}` : 'Publish'}</span>
          <button type="button" className={c.close} onClick={onClose} disabled={busy} aria-label="Close"><i className="material-icons">close</i></button>
        </div>
        <div className={c.body}>
          <section>
            <div className={c.label}>
              <span>{isNew ? 'Sections on this page' : 'Changes since the published version'}</span>
              {page.publishedVersion && <span className={c.mono}>v{page.publishedVersion}</span>}
            </div>
            {isNew && !status.loading && <p className={c.muted} style={{ marginBottom: 8 }} data-testid="publish-new-page">This page isn’t live, so reviewers see the whole page.</p>}
            {loading && <p className={c.muted}>{isNew ? 'Loading…' : 'Comparing…'}</p>}
            {loadError && <p className={c.error}>Couldn’t load the {live.error ? 'published' : 'selected'} version: {loadError.message}</p>}
            {liveMissing && !live.error && <p className={c.error}>The published version v{page.publishedVersion} couldn’t be found, so there’s nothing to compare with.</p>}
            {liveMissing && after && <p className={c.muted}>You can still save this version and ask for review.</p>}
            {diff && !changes.length && !diff.settings.length && <p className={c.muted}>{isNew ? 'No sections yet.' : 'No section changes.'}</p>}
            {diff && (
              <ul className={c.list} data-testid="publish-changes">
                {changes.map((ch, i) => {
                  const clickable = Boolean(!versionId && ch.status !== 'removed' && ch.component._id && onSelectSection);
                  return (
                    <li key={`${ch.status}-${ch.slotId}-${ch.index}-${i}`}>
                      <button
                        type="button"
                        className={`${c.change} ${isNew ? c.plain : ''}`}
                        data-clickable={clickable}
                        data-status={ch.status}
                        tabIndex={clickable ? 0 : -1}
                        onClick={() => { if (clickable && onSelectSection) onSelectSection(ch.component._id as string); }}
                      >
                        {!isNew && <span className={c.status} style={{ color: STATUS[ch.status].color }}><span className={c.dot} style={{ background: STATUS[ch.status].color }} />{STATUS[ch.status].label}</span>}
                        <span className={c.name}>{nameOf(ch.component)}</span>
                        <span className={c.where}>{SLOT_LABELS[ch.slotId] || ch.slotId}</span>
                      </button>
                    </li>
                  );
                })}
                {diff.settings.map(s => (
                  <li key={s.field}>
                    <div className={c.change} data-status="setting">
                      <span className={c.status} style={{ color: S.amber }}><span className={c.dot} style={{ background: S.amber }} />Changed</span>
                      <span className={c.name}>{s.label}</span>
                      <span className={c.where}>{s.before && s.after ? `${s.before} → ${s.after}` : 'Template'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className={c.muted} style={{ marginTop: 10 }}>The title, path and SEO settings aren’t part of versions: they go live with the page’s next deployment.</p>
          </section>

          <section>
            <div className={c.label}><span>Checks</span></div>
            <div data-testid="publish-checks">
              {checks.map(x => (
                <div key={x.id} className={c.check} data-check={x.id} data-level={x.level}>
                  <span className={c.dot} style={{ background: LAMP[x.level], marginTop: 6 }} />
                  <div>
                    {x.text}
                    {x.items && <ul className={c.checkItems}>{x.items.map(it => <li key={it}>{it}</li>)}</ul>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {!reviewVersion && (
            <section className={c.field}>
              <div className={c.label}><span>Version</span><span className={c.version}>{newVersion}</span></div>
              <label className={c.field} htmlFor="publish-note">
                <span>Note for reviewers</span>
                <textarea
                  id="publish-note"
                  className={c.input}
                  rows={3}
                  value={note}
                  onChange={(e) => { setNote(e.target.value); setNoteTouched(true); }}
                  placeholder="What changed and why"
                />
              </label>
            </section>
          )}
          {!page.canUserRequestApproval && <p className={c.muted}>You can save versions; asking for review needs the request-approval role for this category.</p>}
          {error && <p className={c.error} role="alert">{error}</p>}
        </div>
        <div className={c.foot}>
          {page.canUserRequestApproval && (
            <button type="button" className={c.primary} disabled={busy || saving || !ready || !canRequest} onClick={() => run(true)} data-testid="publish-request">
              {busy ? 'Working…' : reviewVersion ? `Ask for review of v${reviewVersion}` : `Save v${newVersion} and ask for review`}
            </button>
          )}
          {!reviewVersion && (
            <button type="button" className={page.canUserRequestApproval ? c.secondary : c.primary} disabled={busy || saving || !ready} onClick={() => run(false)} data-testid="publish-save-only">
              {saving ? 'Waiting for the draft to save…' : `Save v${newVersion} only`}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};

export default PublishSheet;
